import { Channels, MERGE_REASONS } from '@mi-tool/consts';
import { Helpers } from '@mi-tool/utils/helpers';
import {
  PERM_VIEW_ENQUIRY_ANSWER,
  PERM_VIEW_ENQUIRY_GREET_SIGN,
  PERM_VIEW_ENQUIRY_QUESTION,
} from 'app/common/common/constants/permissions';
import { BHistory, BInquirer } from 'app/modules/data-model/data-model.module';
import { MergeReason } from 'app/modules/data-model/interaction/interaction-merge.service';

export type EHistory = BHistory & {
  containerClass: string;
  shouldDisplayChannel: boolean;
  skipTextDisplay: boolean;
  hasTranslatableText: boolean;
  showInfoRow: boolean;
  label?: string;
  mergeLabel?: string;
  mergeReasonKeys?: string[];
  dtlChanges: DetailChange[] | undefined;
  isFollowUp: boolean;
};

export type MergeAsCqAndNewInqData = {
  id: number;
  text: string;
  channel: string;
  mergedFromId?: number;
};

export type DetailChange = {
  rawField: string;
  field: string;
  from: string | BInquirer | MergeAsCqAndNewInqData[] | string[] | number[];
  to: string | BInquirer | MergeAsCqAndNewInqData[] | string[] | number[];
  permissionName?: string;
};

export namespace EHistory {
  export function from(
    history: BHistory[],
    inquirers: BInquirer[],
    helpers: Helpers,
    interactionId: number
  ): EHistory[] {
    const ehs = history as EHistory[];
    DetailChanges.populate(helpers, ehs, inquirers);
    ehs.forEach((eh) => {
      eh.containerClass = resolveContainerClass(eh.category);
      eh.shouldDisplayChannel = shouldDisplayChannel(eh.category);
      eh.skipTextDisplay = ['personal_assignment', 'add_attachment'].includes(eh.category);
      eh.showInfoRow = !['reject_answer_note', 'simultaneous_assignment'].includes(eh.category);
      eh.hasTranslatableText = ['fu_attempt'].includes(eh.category);
      eh.isFollowUp = ['fu_request', 'fu_response', 'resend_fu_request', 'lost_fu'].includes(
        eh.category
      );
      if (eh.mergeOrDuplicateParam) {
        eh.mergeLabel = Label.resolveMergeLabel(eh, interactionId);
        eh.mergeReasonKeys = Label.resolveMergeReasonTranslationKeys(eh);
      } else {
        eh.label = Label.resolve(eh, interactionId);
      }
    });
    return ehs;
  }
}

function resolveContainerClass(category: string): string {
  if (['forward_to_dept', 'forward_to_user', 'fu_request', 'fu_response'].includes(category)) {
    return 'share';
  } else if (['error', 'bounce_detected'].includes(category)) {
    return 'error';
  }
  return '';
}

function shouldDisplayChannel(category: string): boolean {
  return ['answer', 'fu_request', 'fu_response', 'create', 'fu_attempt'].includes(category);
}

namespace Label {
  const categoryToLabel = {
    personal_assignment: 'changed assignment',
    team_assignment: 'changed assignment',
    update_detail: 'updated enquiry detail',
    change_details: 'started review',
    delete_inquiry: 'deleted question',
    other: 'added note',
    reject_answer_note: 'added note',
    error: 'error',
    bounce_detected: 'delivery failure',
    closed_as_merge: 'merged this enquiry into',
    interaction_split: 'split enquiry',
    delete_attachment: 'deleted attachment',
    add_attachment: 'added attachment',
    close_review: 'closed review',
    copy_answer: 'copied answer',
    link_repo_doc: 'linked document from repository',
    unlink_repo_doc: 'unlinked document from repository',
    add_answer: 'submitted for approval',
    add_answer_with_status: 'submitted for approval',
    reject_answer: 'rejected answer',
    reject_answer_with_status: 'rejected answer',
    reject_answer_with_status_revert: 'reverted status',
    approve_answer: 'approved answer',
    send_answer: 'sent answer',
    reopen_answer: 'reopened enquiry for new answer',
    approved_and_send_answer: 'approved and sent answer',
    change_status_to_approved_and_send_answer: 'approved and sent answer',
    updated_answer_due_to_product_update: 'updated answer due to product update',
    updated_answer_due_to_incoming_channel_update: 'updated answer due to incoming channel update',
    updated_answer_due_to_deleted_question: 'updated answer due to deleted question',
    unspam: 'moved the message from spam list to inbox',
    create: 'created',
    change_status: 'changed status',
    change_status_to_answered: 'changed status (saved without email)',
    saved_answer_without_email: 'saved answer without email',
    update_answer: 'updated answer',
    forward_to_dept: 'forwarded interaction data',
    forward_to_user: 'forwarded interaction data',
    fu_request: 'Started follow up',
    fu_response: 'Follow up response',
    access: 'Access',
    answer: 'Answer',
    closed_as_duplicate: 'closed this interaction as a duplicate of',
    duplicate_of_this: 'closed the following interaction as a duplicate of current interaction:',
    merge_as_duplicate: 'merged interaction',
    resend_fu_request: 'SENT_FOLLOW_UP_REMINDER',
    lost_fu: 'CLOSED_INTERACTION_AND_SENT_LOST_TO_FOLLOW_UP_NOTIFICATION',
    delete_pii: 'deleted inquirer personal information',
    added_disclaimer: 'added a disclaimer',
    removed_disclaimer: 'removed a disclaimer',
    updated_disclaimer: 'updated a disclaimer',
    created_via_manual_insert: 'created via manual insert',
    update_team_after_inquirer_country_change:
      'updated inquirer details{INTERACTION_ID/HCP}, which led to changed team in the current interaction',
    update_team_and_assignment_after_inquirer_country_change:
      'updated inquirer details{INTERACTION_ID/HCP}, which led to changed team and assignment in the current interaction',
    personal_assignment_on_role_archive:
      'archived a user role, which led to{ADDITIONAL_INFO}changed assignment in the current interaction',
    create_question_from_fu: 'CREATED_QUESTION_FROM_FU_RESPONSE',
    fu_attempt: 'ATTEMPTED_FOLLOW_UP',
    notification: 'RECEIVES_THE_FOLLOWING_NOTIFICATION',
    add_event_to_int: 'SET_INTERACTION_AS_BEING_PART_OF_AN_EVENT',
    update_event_within_int: 'UPDATED_EVENT',
    remove_event_from_int: 'REMOVED_INTERACTION_AS_BEING_PART_OF_AN_EVENT',
    update_watchers: 'UPDATE_WATCHERS',
  };
  const historyCategoriesForMergedFromLabel = [
    'answer',
    'fu_request',
    'fu_response',
    'other',
    'internal',
    'error',
    'merge_as_new_inquiry',
    'merge_as_inquiry',
    'interaction_merged',
  ];

  export function resolve(history: EHistory, interactionId: number): string {
    if (
      history.category === 'delete_pii' &&
      history.dtlChanges &&
      history.dtlChanges.length === 1
    ) {
      const detailChange = history.dtlChanges[0];
      return detailChange.from === 'YES'
        ? 'UNMARKED_INQUIRER_PII_DELETE'
        : 'MARKED_INQUIRER_PII_DELETE';
    } else if (history.category === 'resend_fu_request' && !history.text) {
      return 'FOLLOW_UP_RESEND';
    } else if (history.category === 'update_team_after_inquirer_country_change') {
      return insertValueInHistoryCategoryLabel(history, 'team_update_details', interactionId);
    } else if (history.category === 'update_team_and_assignment_after_inquirer_country_change') {
      return insertValueInHistoryCategoryLabel(
        history,
        'assignment_removal_on_team_update_details',
        interactionId
      );
    } else if (history.category === 'personal_assignment_on_role_archive') {
      return insertValueInHistoryCategoryLabel(
        history,
        'assignment_removal_on_role_archive_details',
        interactionId
      );
    } else if (
      ['added_disclaimer', 'removed_disclaimer', 'updated_disclaimer'].includes(history.category)
    ) {
      return resolveDiscHistoryLabel(history);
    } else if (history.category === 'interaction_references') {
      return resolveReferencesHistoryLabel(history);
    }
    return categoryToLabel[history.category] || history.category;
  }

  export function resolveMergeLabel(history: EHistory, interactionId: number): string {
    if (historyCategoriesForMergedFromLabel.includes(history.category)) {
      return 'MERGED_FROM';
    } else if (history.category === 'interaction_split') {
      return history.mergeOrDuplicateParam === interactionId
        ? 'split within this enquiry'
        : history.interactionId === interactionId
        ? 'split from different enquiry'
        : 'split to separate enquiry';
    } else {
      return resolve(history, interactionId);
    }
  }

  export function resolveMergeReasonTranslationKeys(history: BHistory): string[] {
    const mergeReason = resolveMergeReason(history);
    return MergeReason.getTranslationKeys(mergeReason);
  }

  function insertValueInHistoryCategoryLabel(
    history: BHistory,
    historyKey: string,
    interactionId: number
  ): string {
    const keyValue = JSON.parse(history.text)[historyKey][1];
    if (history.category === 'personal_assignment_on_role_archive') {
      const replacement = keyValue ? ' user deactivation and ' : ' ';
      return categoryToLabel[history.category].replace('{ADDITIONAL_INFO}', replacement);
    } else {
      let replacement = ' in HCP administration';
      if (keyValue && keyValue !== interactionId) {
        replacement = ' in Interaction ID: ' + keyValue;
      } else if (keyValue && keyValue === interactionId) {
        replacement = '';
      }
      return categoryToLabel[history.category].replace('{INTERACTION_ID/HCP}', replacement);
    }
  }

  function resolveDiscHistoryLabel(history: EHistory): string {
    const action = history.dtlChanges.find(
      (h) =>
        ['REASON_FOR_INCLUSION', 'REASON_FOR_REMOVAL', 'REASON_FOR_UPDATE'].includes(h.field) &&
        !['reason_for_removal_from_interaction', 'reason_for_inclusion_from_interaction'].includes(
          h.rawField
        )
    )?.from;
    if (!action) {
      return categoryToLabel[history.category] || history.category;
    }
    return `${action} and subsequently ${categoryToLabel[history.category]} ${
      history.category !== 'removed_disclaimer' ? 'into' : 'from'
    } the current interaction`;
  }

  function resolveReferencesHistoryLabel(history: EHistory): string {
    let history_label = '';
    const interaction_reference_id = history.dtlChanges[0];

    if (interaction_reference_id.from !== 'BLANK' && interaction_reference_id.to !== 'BLANK') {
      history_label = 'updated interaction reference ID';
    } else if (interaction_reference_id.from !== 'BLANK') {
      history_label = 'deleted interaction reference ID';
    } else {
      history_label = 'added interaction reference ID';
    }

    history.dtlChanges = history.dtlChanges.filter((item) => item.rawField !== 'ID');
    return history_label;
  }

  function resolveMergeReason(history: BHistory): MergeReason {
    let mergeReason;
    if (history.category === 'closed_as_merge' && history.historyDescription) {
      mergeReason = history.historyDescription['mergeReason'];
    } else if (history.category === 'fu_response' && history.historyDescription) {
      mergeReason = MERGE_REASONS.followUpResponse;
    } else if (history.category === 'other' && history.historyDescription) {
      mergeReason = MERGE_REASONS.thankYouMessage;
    } else {
      const mergeReasonMap = {
        internal: MERGE_REASONS.internalMessage,
        error: MERGE_REASONS.errorMessage,
        merge_as_new_inquiry: MERGE_REASONS.newInquiry,
        merge_as_inquiry: MERGE_REASONS.clarificationQuestion,
        merge_as_duplicate: MERGE_REASONS.duplicate,
      };
      mergeReason = mergeReasonMap[history.category];
    }
    return mergeReason;
  }
}

namespace DetailChanges {
  export function populate(helpers: Helpers, history: EHistory[], inquirers: BInquirer[]): void {
    history
      .filter((h) => jsonDetailChangeCategories.includes(h.category) || isCustomJsonDetailChange(h))
      .forEach((h) => {
        adjustSplitHistory(h);
        h.dtlChanges = decodeData(helpers, h, inquirers);
      });
    for (let i = 0; i < history.length; ++i) {
      if (
        history[i].category === 'update_detail' &&
        history[i + 1].category === 'create' &&
        history[i + 1].channel === 'manual insert'
      ) {
        history[i].category = 'created_via_manual_insert';
        history.splice(i + 1, 1);
      }

      if (history[i].category === 'add_attachment' && history[i].text) {
        history[i].text = fieldNameMap[history[i].text];
      }

      if (history[i].category === 'approve_answer') {
        if (
          history[i + 1]?.category === 'change_status' &&
          history[i + 1].dtlChanges[0].to.toString().includes('Answered') &&
          history[i + 2]?.category === 'send_answer'
        ) {
          history[i + 1].category = 'change_status_to_approved_and_send_answer';
          history.splice(i + 2, 1);
          history.splice(i, 1);
        } else if (history[i - 1]?.category === 'send_answer') {
          history[i].category = 'approved_and_send_answer';
          history.splice(i - 1, 1);
        } else if (history[i + 1]?.category === 'send_answer') {
          history[i].category = 'approved_and_send_answer';
          history.splice(i + 1, 1);
        }
      }

      if (
        history[i].category === 'reject_answer' &&
        ['change_status', 'revert_status'].includes(history[i - 1]?.category)
      ) {
        if (history[i + 1]?.category === 'other') {
          history[i + 1].category = 'reject_answer_note';
        }
        history[i - 1].category =
          history[i - 1]?.category === 'change_status'
            ? 'reject_answer_with_status'
            : 'reject_answer_with_status_revert';
        history.splice(i, 1);
      }

      if (
        history[i].category === 'change_status' &&
        history[i].dtlChanges[0].to.toString().includes('Answered') &&
        history[i + 1]?.category === 'send_answer'
      ) {
        history[i].category = 'change_status_to_answered';
        history.splice(i + 1, 1);
      }

      if (history[i].category === 'send_answer' && history[i].text) {
        history[i].category = 'saved_answer_without_email';
        history[i].text = null;
      }

      if (history[i].category === 'add_answer' && history[i - 1]?.category === 'change_status') {
        history[i - 1].category = 'add_answer_with_status';
        history.splice(i, 1);
      }

      if (
        (history[i].category === 'personal_assignment' &&
          history[i - 1]?.category === 'team_assignment') ||
        (history[i].category === 'team_assignment' &&
          history[i - 1]?.category === 'personal_assignment')
      ) {
        if (history[i].category === 'personal_assignment') {
          [history[i], history[i - 1]] = [history[i - 1], history[i]];
        }
        history[i].category = 'simultaneous_assignment';
      }

      if (history[i].category === 'team_assignment' && history[i].dtlChanges.length > 1) {
        if (history[i].dtlChanges[0].rawField.toLowerCase() === 'team_update_details') {
          history[i].category = 'update_team_after_inquirer_country_change';
        } else if (
          history[i].dtlChanges[0].rawField.toLowerCase() ===
          'assignment_removal_on_team_update_details'
        ) {
          history[i].category = 'update_team_and_assignment_after_inquirer_country_change';
        } else if (
          history[i].dtlChanges[1].rawField.toLowerCase() ===
          'assignment_removal_on_role_archive_details'
        ) {
          history[i].category = 'personal_assignment_on_role_archive';
        }
      }

      if (history[i].category === 'update_answer' && history[i].createdBy?.username === 'system') {
        if (
          !history[i].dtlChanges[0].field.includes('subject_update_due_to_') &&
          history[i].dtlChanges[0].rawField.toLowerCase() === 'subject' &&
          history[i + 1].category === 'update_detail'
        ) {
          if (history[i + 1].dtlChanges[0].rawField.toLowerCase() === 'product') {
            history[i].category = 'updated_answer_due_to_product_update';
          }
          if (
            history[i + 1].dtlChanges[0].rawField.toLowerCase() === 'incoming_channel' ||
            history[i + 1].text.toLowerCase().includes('incoming_channel')
          ) {
            history[i].category = 'updated_answer_due_to_incoming_channel_update';
          }
        }
        if (history[i].dtlChanges[0].field.includes('subject_update_due_to_')) {
          const category_sub_string = 'updated_answer_due_to_';
          if (history[i].dtlChanges[0].field.includes('product')) {
            history[i].category = category_sub_string + 'product_update';
          } else if (history[i].dtlChanges[0].field.includes('incoming_channel')) {
            history[i].category = category_sub_string + 'incoming_channel_update';
          } else if (history[i].dtlChanges[0].field.includes('deleted_question')) {
            history[i].category = category_sub_string + 'deleted_question';
          }
          history[i].dtlChanges[0].field = fieldNameMap['subject'];
        }
      }
      parseHistoryDescription(helpers, history[i], inquirers);
    }
  }

  const jsonDetailChangeCategories = [
    'update_detail',
    'copy_answer',
    'delete_pii',
    'revert_status',
    'change_status',
    'change_status_to_answered',
    'change_status_to_approved_and_send_answer',
    'update_answer',
    'reject_answer_with_status',
    'reject_answer_with_status_revert',
    'add_answer_with_status',
    'added_disclaimer',
    'removed_disclaimer',
    'updated_disclaimer',
    'update_team_after_inquirer_country_change',
    'update_team_and_assignment_after_inquirer_country_change',
    'personal_assignment_on_role_archive',
    'notification',
    'interaction_split',
    'team_assignment',
    'add_event_to_int',
    'update_event_within_int',
    'remove_event_from_int',
    'forward_to_dept',
    'forward_to_user',
    'update_watchers',
    'interaction_references',
    'created_via_manual_insert',
  ];
  const categoriesWithCustomJson = ['merge_as_inquiry', 'closed_as_merge', 'merge_as_new_inquiry'];
  const mergeHistoryCategories = [
    'fu_response',
    'other',
    'internal',
    'error',
    'merge_as_new_inquiry',
    'merge_as_inquiry',
    'merge_as_duplicate',
    'interaction_merged',
    'closed_as_merge',
  ];
  const fieldNameMap = {
    delete_pi_on_close: 'DELETE_PERSONAL_DATA',
    adr_related: 'ADR_RELATED',
    adr_related_reference_ids: 'ADR_REF_IDS',
    off_label: 'OFF_LABEL',
    product_complaint: 'PRODUCT_QUALITY_COMPLAINT',
    product_complaint_reference_ids: 'PQC_REF_IDS',
    topic: 'TOPIC',
    category: 'CATEGORY',
    product: 'PRODUCT',
    product_resolved_via_ai: 'PRODUCT_RESOLVED_VIA_AI',
    priority: 'PRIORITY',
    outgoing_channel: 'OUT_CHANNEL',
    need_srd: 'SRD_NEEDED',
    answer_unavailable: 'ANSWER_UNAVAILABLE',
    incoming_channel: 'INC_CHANNEL',
    question: 'QUESTION_TEXT',
    question_id: 'QUESTION_ID',
    question_ids: 'QUESTION_IDS',
    answer_draft: 'ANSWER_TEXT',
    answer: 'ANSWER_TEXT',
    inquirer: 'INQUIRER',
    subject: 'EMAIL_SUBJECT',
    interaction_title: 'INTERACTION_TITLE',
    greetings: 'Greetings',
    signature: 'Signature',
    disclaimer: 'Disclaimer',
    disclaimer_info: 'DISCLAIMER_INFO',
    reason_for_replacement: 'REASON_FOR_REPLACEMENT',
    reason_for_removal: 'REASON_FOR_REMOVAL',
    reason_for_removal_from_interaction: 'REASON_FOR_REMOVAL',
    reason_for_inclusion: 'REASON_FOR_INCLUSION',
    reason_for_inclusion_from_interaction: 'REASON_FOR_INCLUSION',
    template_id: 'ID',
    reason_for_update: 'REASON_FOR_UPDATE',
    reason_for_disc_update: 'REASON_FOR_UPDATE',
    gdpr_notification: 'NOTIFICATION_SENT_TO_THE_INQ',
    delete_pii: 'DELETE_INQUIRER_PII',
    team: 'TEAM',
    assignment: 'ASSIGNMENT',
    due_date: 'DUE_DATE',
    text_before_split: 'QUESTION_TEXT_BEFORE_SPLIT',
    text_after_split: 'QUESTION_TEXT_AFTER_SPLIT',
    text_to_split: 'QUESTION_TEXT_AFTER_SPLIT',
    reference_question_id: 'QUESTION_ID',
    reference_source: 'REFERENCE_SOURCE',
    reference_id: 'REFERENCE_ID',
    reference_type: 'REFERENCE_TYPE',
  };

  function isCustomJsonDetailChange(history: BHistory): boolean {
    if (!categoriesWithCustomJson.includes(history.category)) {
      return false;
    }
    return isHistoryTextJson(history);
  }

  function adjustSplitHistory(history: BHistory): void {
    if (history.category === 'interaction_split' && history.text) {
      const isJson = isHistoryTextJson(history);
      if (!isJson) {
        history.text = JSON.stringify({ text_before_split: [history.text] });
      }
    }
  }

  function isHistoryTextJson(history: BHistory): boolean {
    try {
      const parsedText = JSON.parse(history.text);
      return Boolean(parsedText && typeof parsedText === 'object');
    } catch (error) {
      return false;
    }
  }

  function parseHistoryDescription(
    helpers: Helpers,
    history: BHistory,
    inquirers: BInquirer[]
  ): void {
    if (
      ((mergeHistoryCategories.includes(history.category) && history.mergeOrDuplicateParam) ||
        ['fu_request', 'fu_response'].includes(history.category)) &&
      history.historyDescription &&
      typeof history.historyDescription === 'string'
    ) {
      history.historyDescription = JSON.parse(history.historyDescription);

      const inquirerId = history.historyDescription?.['inquirerId'];
      if (inquirerId && history.category === 'fu_response') {
        history.historyDescription['inquirer'] = mapValue(
          helpers,
          inquirerId,
          'inquirer',
          inquirers
        );
      }
    }
  }

  function decodeData(helpers: Helpers, history: BHistory, inquirers: BInquirer[]): DetailChange[] {
    try {
      const parsed = JSON.parse(history.text);
      return Object.keys(parsed).map((field) => {
        return {
          rawField: field,
          field: fieldNameMap[field] || field,
          from: mapValue(helpers, parsed[field][0], field, inquirers, true),
          to: mapValue(helpers, parsed[field][1], field, inquirers),
          permissionName: resolvePermission(field),
        };
      });
    } catch (err) {
      console.error('Corrupted data for history: ' + history.id + ' ' + err);
      return [];
    }
  }

  function mapValue(
    helpers: Helpers,
    value: any,
    field: string,
    inquirers: BInquirer[],
    isFromValue: boolean = false
  ): string | BInquirer | MergeAsCqAndNewInqData[] | number[] | string[] {
    if (field === 'text_to_split') {
      return value;
    }
    const isUnset = (val) =>
      val === undefined || val === null || (typeof val === 'string' && val.trim().length === 0);
    const mapPriority = (val) =>
      val === 'normal' ? 'NORMAL' : val === 'high' ? 'HIGH' : val === 'critical' ? 'URGENT' : val;
    if (isUnset(value)) {
      return 'BLANK';
    } else if (value === true) {
      return 'YES';
    } else if (value === false) {
      return 'NO';
    } else if (field === 'inquirer' || field === 'inquirer_details') {
      return inquirers.find((i) => i.pk() == value) || value.toString();
    } else if (field === 'priority') {
      return mapPriority(value);
    } else if (field === 'incoming_channel' || field === 'outgoing_channel') {
      return Channels.resolveTranslationKey(value);
    } else if (['merge_as_new_inquiry', 'merge_as_inquiry'].includes(field)) {
      if (isFromValue) {
        value.forEach((sourceQ) => {
          sourceQ.channel = Channels.resolveTranslationKey(sourceQ.channel);
        });
      } else if (field === 'merge_as_inquiry') {
        value = helpers.arrayOfItemsToText(
          value.map((obj) => obj.id.toString()),
          false
        );
      }
      return value;
    } else if (
      ['Added watcher', 'Added watchers', 'Removed watcher', 'Removed watchers'].includes(field)
    ) {
      if (isFromValue) {
        return value.split(',').map((item) => item.trim());
      }
      return JSON.parse(value);
    } else if (field === 'inserted_from_draft' && isFromValue) {
      return JSON.parse(value);
    } else if (field == 'notification' && !isFromValue) {
      try {
        const parsedValue = JSON.parse(value);
        return typeof parsedValue === 'object' && parsedValue !== null ? parsedValue : value;
      } catch {
        return value;
      }
    } else {
      return value.toString();
    }
  }

  function resolvePermission(field: string): string {
    switch (field) {
      case 'signature':
      case 'greetings':
        return PERM_VIEW_ENQUIRY_GREET_SIGN;
      case 'answer':
      case 'answer_draft':
        return PERM_VIEW_ENQUIRY_ANSWER;
      case 'question':
        return PERM_VIEW_ENQUIRY_QUESTION;
    }
    return undefined;
  }
}
